import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { HasProduct, ProductContextModel, withProduct } from "@insite/client-framework/Components/ProductContext";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import { CartLineModel, OrderLineModel } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import setSearchTerm from "@tSchaedlerYesco/Store/Data/SearchTerm/Handlers/setSearchTerm";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel | OrderLineModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductDescriptionStyles;
}

type Props = OwnProps & HasCategoryContext;

export interface ProductDescriptionStyles {
    productDetailLink?: LinkPresentationProps;
    productDetailDescription?: TypographyProps;
}

export const productDescriptionStyles: ProductDescriptionStyles = {
    productDetailLink: {
        css: css`
            width: 100%;
        `,
        typographyProps: {
            css: css`
                width: 100%;
                ${wrapInContainerStyles}
            `,
        },
    },
};

const ProductDescriptionGtmPlp = ({ product, onClick, extendedStyles, category }: Props) => {
    const dispatch = useDispatch();
    const location = useAppSelector(state => getLocation(state));
    const pagination = useAppSelector(state => getProductListDataViewProperty(state, "pagination"));
    const { billToId, shipToId } = useAppSelector(state => getSession(state));
    const billToState = useAppSelector(state => getBillToState(state, billToId));
    const shipToState = useAppSelector(state => getShipToState(state, shipToId));
    const { value: shipTo } = shipToState;
    const { value: billTo } = billToState;
    const { query } = parseQueryString<{ query: string }>(location.search);
    const [styles] = useState(() => mergeToNew(productDescriptionStyles, extendedStyles));
    const description =
        "product" in product
            ? product.product.productTitle
            : product.shortDescription || ("description" in product ? product.description : "");
    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;
    const Pricing = "product" in product ? product.productInfo.pricing?.actualPriceDisplay : "0.00";
    const extractedPrice = Pricing?.match(/[\d.]+/)?.[0] || "0.00";
    const title =
        "product" in product
            ? product.product.productTitle
            : product.shortDescription || ("description" in product ? product.productName : "");
    const id =
        "product" in product
            ? product.product.id
            : product.shortDescription || ("description" in product ? product.id : "");

    const addSearchTerm = () => {
        // Datalayer Logic
        const dataLayer = (window as any).dataLayer || [];
        dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
        dataLayer.push({
            event: "select_item",
            ecommerce: {
                item_list_id: category?.id || query,
                item_list_name: category?.name || query,
                items: [
                    {
                        item_id: id,
                        item_name: title,
                        price: extractedPrice,
                    },
                ],
            },
        });
        console.log("select_item", dataLayer);

        if (query && billTo?.customerNumber !== undefined) {
            const totalItemCount = pagination?.totalItemCount;
            const productNumber = "product" in product ? product.product.productNumber : "";
            dispatch(
                setSearchTerm({
                    searchTerm: {
                        searchedKeyword: query,
                        noOfProductResults: totalItemCount ?? 0,
                        customerBillToNumber: billTo.customerNumber,
                        customerShipToNumber: shipTo?.customerSequence ?? "",
                        visitedProductNumber: productNumber,
                    },
                }),
            );
        }
    };

    return productDetailPath && (!("isActiveProduct" in product) || product.isActiveProduct) ? (
        <Link
            onClick={addSearchTerm}
            {...styles.productDetailLink}
            href={productDetailPath}
            alt={`See product details for ${description}`}
            data-test-selector="productDescriptionLink"
        >
            {description}
        </Link>
    ) : (
        <Typography {...styles.productDetailDescription}>{description}</Typography>
    );
};

export default withCategory(ProductDescriptionGtmPlp);
